import {
  LayersIcon,
  WavesIcon,
  BikeIcon,
  FootprintsIcon,
  AppleIcon,
  FlagIcon,
  CalendarDaysIcon,
  ZapIcon,
  ClipboardCheckIcon,
  ActivityIcon,
} from 'lucide-react';

export const phases = [
  {
    n: '01',
    name: 'Fundamentos del triatlón',
    short: 'Construye tus bases.',
    tag: 'Bases',
    icon: LayersIcon,
    title: 'Fundamentos del triatlón',
    lead: 'Comprende las bases de tu preparación.',
    desc: 'Construye una visión general del triatlón y comprende cómo se relacionan sus tres disciplinas dentro de un mismo objetivo deportivo. Este volumen establece los fundamentos sobre los que se organiza el resto del sistema.',
  },
  {
    n: '02',
    name: 'Dominio de la natación',
    short: 'Desarrolla tu preparación en el agua.',
    tag: 'Natación',
    icon: WavesIcon,
    title: 'Dominio de la natación',
    lead: 'Organiza tu preparación en el agua.',
    desc: 'Comprende los elementos fundamentales de la natación dentro del triatlón y cómo integrarla en tu rutina de entrenamiento.',
  },
  {
    n: '03',
    name: 'Potencia sobre la bicicleta',
    short: 'Trabaja tu rendimiento sobre dos ruedas.',
    tag: 'Ciclismo',
    icon: BikeIcon,
    title: 'Potencia sobre la bicicleta',
    lead: 'Integra el ciclismo en tu preparación global.',
    desc: 'Organiza el trabajo sobre la bicicleta como parte de un proceso que también contempla la natación, la carrera y la recuperación.',
  },
  {
    n: '04',
    name: 'Resistencia y velocidad en carrera',
    short: 'Prepara tus sesiones de carrera con un enfoque estructurado.',
    tag: 'Carrera',
    icon: FootprintsIcon,
    title: 'Resistencia y velocidad en carrera',
    lead: 'Trabaja los fundamentos de tu preparación a pie.',
    desc: 'Comprende la importancia de organizar el trabajo de resistencia y velocidad dentro de una preparación que contempla las exigencias de las tres disciplinas.',
  },
  {
    n: '05',
    name: 'Planificación y nutrición deportiva',
    short: 'Organiza tu preparación y presta atención a la alimentación.',
    tag: 'Nutrición',
    icon: AppleIcon,
    title: 'Planificación y nutrición deportiva',
    lead: 'Organiza el entrenamiento y presta atención a la alimentación.',
    desc: 'Integra los fundamentos de planificación, nutrición e hidratación en tu preparación, teniendo en cuenta las necesidades individuales y las exigencias de las sesiones.',
  },
  {
    n: '06',
    name: 'Estrategia y día de competencia',
    short: 'Convierte tu preparación en un plan para el día de la prueba.',
    tag: 'Competición',
    icon: FlagIcon,
    title: 'Estrategia y día de competencia',
    lead: 'Prepara los aspectos fundamentales de tu competición.',
    desc: 'Reúne los elementos de planificación, preparación y estrategia que necesitas considerar cuando se acerca el momento de competir.',
  },
];

export const tools = [
  {
    verb: 'Planificador',
    name: 'Planificador de Entrenamiento de 12 Semanas™',
    desc: 'Una herramienta visual para organizar tu preparación, semana a semana y sesión a sesión.',
    icon: CalendarDaysIcon,
  },
  {
    verb: 'Preparación',
    name: 'Checklist Definitivo del Triatleta™',
    desc: 'Una guía de comprobación para organizar los elementos importantes antes del día de competición.',
    icon: ClipboardCheckIcon,
  },
  {
    verb: 'Nutrición',
    name: 'Guía de Nutrición e Hidratación™',
    desc: 'Un recurso para organizar la alimentación y la hidratación durante la preparación de resistencia.',
    icon: ZapIcon,
  },
  {
    verb: 'Seguimiento',
    name: 'Registro de Rendimiento del Triatleta™',
    desc: 'Una herramienta para registrar tu actividad y observar tu evolución en las tres disciplinas.',
    icon: ActivityIcon,
  },
];

export const systemIncludes = [
  'Fundamentos del entrenamiento de natación',
  'Estructuras de preparación sobre la bicicleta',
  'Conceptos de progresión en carrera',
  'Trabajo complementario de fuerza',
  'Consideraciones sobre recuperación',
  'Organización del entrenamiento',
  'Seguimiento del progreso',
  'Fundamentos de nutrición deportiva',
  'Consideraciones de hidratación',
  'Preparación para la competición',
  'Conceptos de estrategia de carrera',
  'Listas prácticas de comprobación',
];

export type BonusAccent = 'electric' | 'perf' | 'ember' | 'aqua';

export const bonuses: {
  n: string;
  accent: BonusAccent;
  title: string;
  subtitle: string;
  desc: string[];
  tags: string[];
  price: string;
  cover: string;
}[] = [
  {
    n: '01',
    accent: 'electric',
    title: 'Planificador de Entrenamiento de 12 Semanas™',
    subtitle: 'Tu herramienta visual para organizar la preparación semana a semana.',
    desc: [
      'Deja de preguntarte cómo distribuir tu entrenamiento durante las próximas semanas.',
      'El Planificador de Entrenamiento de 12 Semanas™ te ayuda a visualizar tu preparación y organizar tus sesiones con mayor claridad.',
    ],
    tags: ['Planificación de doce semanas', 'Organización de las sesiones', 'Seguimiento de la rutina', 'Estructura para las tres disciplinas'],
    price: '$32',
    cover: 'https://cdn.magicpatterns.com/uploads/jy18YjpNJMugYehMtPjy8P/Checklist_Definitivo_del_Triatleta_(1).webp',
  },
  {
    n: '02',
    accent: 'perf',
    title: 'Guía de Nutrición e Hidratación™',
    subtitle: 'Tu recurso para organizar la alimentación y la hidratación durante el entrenamiento de resistencia.',
    desc: [
      'El entrenamiento exige energía. La preparación también requiere prestar atención a la forma en que organizas tu alimentación y tu hidratación.',
      'La Guía de Nutrición e Hidratación™ te ayuda a comprender y organizar estos aspectos de tu preparación.',
    ],
    tags: ['Alimentación cotidiana', 'Nutrición antes de entrenar', 'Hidratación y electrolitos', 'Alimentación durante las sesiones', 'Nutrición para la recuperación', 'Preparación nutricional para competir'],
    price: '$29',
    cover: 'https://cdn.magicpatterns.com/uploads/pkFzQLbPgymQYkKZe9XKp9/Checklist_Definitivo_del_Triatleta_(2).webp',
  },
  {
    n: '03',
    accent: 'ember',
    title: 'Checklist Definitivo del Triatleta™',
    subtitle: 'Tu lista de comprobación para organizar los días previos a la competición.',
    desc: [
      'La última semana no debería convertirse en una colección de decisiones de último momento.',
      'El Checklist Definitivo del Triatleta™ te ayuda a revisar y organizar los elementos importantes de tu preparación.',
    ],
    tags: ['Planificación', 'Viaje', 'Equipamiento', 'Preparación de las tres disciplinas', 'Mañana de carrera', 'Línea de salida'],
    price: '$32',
    cover: 'https://cdn.magicpatterns.com/uploads/weFGAnj2Bq1mraJkiWSpwX/Checklist_Definitivo_del_Triatleta.webp',
  },
  {
    n: '04',
    accent: 'aqua',
    title: 'Registro de Rendimiento del Triatleta™',
    subtitle: 'Registra tu entrenamiento. Comprende tu evolución. Mantén la constancia.',
    desc: [
      'El entrenamiento se vuelve más fácil de revisar cuando tienes tus registros organizados.',
      'Utiliza el Registro de Rendimiento del Triatleta™ para llevar un seguimiento de tu actividad en las tres disciplinas y observar tu preparación a lo largo del tiempo.',
    ],
    tags: ['Natación', 'Ciclismo', 'Carrera', 'Registro de actividad', 'Seguimiento de recuperación', 'Organización semanal'],
    price: '$27',
    cover: 'https://cdn.magicpatterns.com/uploads/wjXq44h1u942nP4NuUHLEk/Checklist_Definitivo_del_Triatleta_(3).webp',
  },
];

export const idealFor = [
  'Triatletas que quieren organizar su preparación.',
  'Deportistas de resistencia que buscan una estructura de entrenamiento más clara.',
  'Atletas que necesitan integrar natación, ciclismo y carrera.',
  'Personas que están comenzando y necesitan comprender los fundamentos del triatlón.',
  'Deportistas con experiencia que quieren organizar mejor su proceso de preparación.',
];

export const offerIncludes = [
  'Fundamentos del Triatlón',
  'Dominio de la Natación',
  'Potencia sobre la Bicicleta',
  'Resistencia y Velocidad en Carrera',
  'Planificación y Nutrición Deportiva',
  'Estrategia y Día de Competencia',
];
